import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Group, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { Tabs, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import type { logs } from '@repletoryreach/sdk-api';
import { CopyableId } from '../../../components/CopyableId';
import { TenantName } from '../../../components/TenantName';
import { formatDateTime, formatRelative } from '../../../lib/format';
import { useT } from '../../../i18n/I18nProvider';
import { levelColor, levelLabel, primarySummary, statusColor } from '../columns';
import './log-detail.css';

const MUTED = 'var(--rr-color-text-muted)';

function Field({ label, children }: { label: string; children: ReactNode }) {
  if (children === null || children === undefined || children === '') return null;
  return (
    <div className="rr-log-field">
      <Text role="caption" c={MUTED}>{label}</Text>
      <div className="rr-log-field__value">{children}</div>
    </div>
  );
}

/** A column the row may or may not carry, rendered only when it does. */
function value(row: logs.LogRow, key: string): string | null {
  const v = row[key];
  if (v === null || v === undefined || v === '') return null;
  return String(v);
}

function jsonPanel(row: logs.LogRow, key: string, empty: string): ReactNode {
  const raw = row[key];
  if (raw === null || raw === undefined || raw === '') return <EmptyState title={empty} />;
  const text = typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2);
  return <pre className="rr-log-json">{text}</pre>;
}

/**
 * One log row, opened from the table.
 *
 * Everything here is already in the row: the query returns the family's columns
 * plus the payload, properties and exception documents, and no separate read
 * exists. The correlation id keeps its copy affordance and gets the action an
 * operator actually wants from it, which is every other row that carries it.
 */
export function LogDetailDrawer({ row, family, onClose, onPrevious, onNext }: {
  row: logs.LogRow | null;
  family: logs.LogFamily;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
}) {
  const t = useT();
  const nav = useNavigate();

  return (
    <Drawer opened={row !== null} onClose={onClose} title={t('logs.detail.title')} size="lg">
      {row && (
        <Stack gap="lg">
          <Group gap="sm" align="center" wrap="nowrap">
            <Badge size="sm" color={levelColor(row.level)} variant="light">{levelLabel(row.level)}</Badge>
            <Title order={5}>{primarySummary(t, family, row)}</Title>
          </Group>
          <Text role="caption" c={MUTED}>
            {formatDateTime(row.occurred_at)} · {formatRelative(row.occurred_at)}
          </Text>

          <Tabs defaultValue="details">
            <Tabs.List>
              <Tabs.Tab value="details">{t('logs.detail.tab.details')}</Tabs.Tab>
              <Tabs.Tab value="payload">{t('logs.detail.tab.payload')}</Tabs.Tab>
              <Tabs.Tab value="properties">{t('logs.detail.tab.properties')}</Tabs.Tab>
              <Tabs.Tab value="exception">{t('logs.detail.tab.exception')}</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="details">
              <Stack gap={0}>
                <Field label={t('logs.detail.family')}>{t(`logs.family.${family}`)}</Field>
                <Field label={t('logs.columns.service')}>{value(row, 'service_name')}</Field>
                <Field label={t('logs.filters.env')}>{value(row, 'environment')}</Field>
                <Field label={t('logs.columns.level')}>{levelLabel(row.level)}</Field>
                {value(row, 'status_code') && (
                  <Field label={t('common.status')}>
                    <Badge size="xs" color={statusColor(row.status_code)} variant="light">
                      {value(row, 'status_code')}
                    </Badge>
                  </Field>
                )}
                <Field label={t('logs.columns.method')}>{value(row, 'method')}</Field>
                <Field label={t('logs.columns.path')}>{value(row, 'path')}</Field>
                <Field label={t('logs.columns.ms')}>{value(row, 'duration_ms')}</Field>
                <Field label={t('logs.columns.rows')}>{value(row, 'row_count')}</Field>
                <Field label={t('logs.columns.event')}>{value(row, 'event_code')}</Field>
                <Field label={t('logs.columns.kind')}>{value(row, 'kind')}</Field>
                <Field label={t('logs.columns.message')}>{value(row, 'message')}</Field>
                <Field label={t('logs.detail.host')}>{value(row, 'host')}</Field>
                {/* The organisation is a name here, as everywhere else; the id
                    behind it stays in the tooltip. */}
                {value(row, 'org_id') && (
                  <Field label={t('audit.detail.organisation')}>
                    <TenantName id={String(row.org_id)} />
                  </Field>
                )}
                {row.correlation_id && (
                  <Field label={t('logs.columns.correlation')}>
                    <CopyableId value={String(row.correlation_id)} label={t('logs.copyCorrelation')} />
                  </Field>
                )}
                <Field label={t('logs.detail.occurred')}>{formatDateTime(row.occurred_at)}</Field>
              </Stack>
            </Tabs.Panel>
            <Tabs.Panel value="payload">{jsonPanel(row, 'payload', t('logs.detail.noPayload'))}</Tabs.Panel>
            <Tabs.Panel value="properties">{jsonPanel(row, 'properties', t('logs.detail.noProperties'))}</Tabs.Panel>
            <Tabs.Panel value="exception">{jsonPanel(row, 'exception', t('logs.detail.noException'))}</Tabs.Panel>
          </Tabs>

          {/* Following one request across services is the whole reason the
              correlation id is on the row. The query page is where a search
              across families and a time window already live, so this hands the
              id to that page rather than half-implementing it here. */}
          {row.correlation_id && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => nav(`/logs/query?family=${family}&correlation_id=${encodeURIComponent(String(row.correlation_id))}`)}
            >
              {t('logs.detail.findCorrelated')}
            </Button>
          )}

          {(onPrevious || onNext) && (
            <Group justify="space-between" align="center">
              <Button size="xs" variant="subtle" onClick={onPrevious} disabled={!onPrevious}>
                {t('audit.detail.previous')}
              </Button>
              <Button size="xs" variant="subtle" onClick={onNext} disabled={!onNext}>
                {t('audit.detail.next')}
              </Button>
            </Group>
          )}
        </Stack>
      )}
    </Drawer>
  );
}
